import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { inAppNotificationService, Notification } from '@/services/inAppNotificationService';
import { useAuth } from './useAuth';
import { useToast } from './use-toast';

export function useNotifications() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: notifications = [], isLoading } = useQuery({
    queryKey: ['notifications', user?.id],
    queryFn: () => user ? inAppNotificationService.getNotifications(user.id, 20) : [],
    enabled: !!user
  });

  const { data: unreadCount = 0 } = useQuery({
    queryKey: ['notifications-unread-count', user?.id],
    queryFn: () => user ? inAppNotificationService.getUnreadCount(user.id) : 0,
    enabled: !!user,
    refetchInterval: 30000 // Refetch every 30 seconds
  });

  const markAsReadMutation = useMutation({
    mutationFn: (notificationId: string) => 
      inAppNotificationService.markAsRead(notificationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
    }
  });

  const markAllAsReadMutation = useMutation({
    mutationFn: () => 
      user ? inAppNotificationService.markAllAsRead(user.id) : Promise.resolve(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
      toast({
        title: 'All notifications marked as read'
      });
    }
  });

  const deleteNotificationMutation = useMutation({
    mutationFn: (notificationId: string) =>
      inAppNotificationService.deleteNotification(notificationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
    }
  });

  // Real-time subscription
  useEffect(() => {
    if (!user) return;

    const unsubscribe = inAppNotificationService.subscribeToNotifications(
      user.id,
      (notification: Notification) => {
        // Show toast for new notifications
        toast({
          title: notification.title,
          description: notification.message,
          duration: 5000
        });

        // Invalidate queries to refresh UI
        queryClient.invalidateQueries({ queryKey: ['notifications'] });
        queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
      }
    );

    return unsubscribe;
  }, [user, toast, queryClient]);

  return {
    notifications,
    unreadCount,
    isLoading,
    markAsRead: markAsReadMutation.mutate,
    markAllAsRead: markAllAsReadMutation.mutate,
    deleteNotification: deleteNotificationMutation.mutate
  };
}
